import { DateHelper, NameHelper } from "@swivl-mono/helpers";
import { Channel, EscalationInterface, EscalationStatus, SessionInterface, UserInterface } from "@swivl-mono/types";

import { Member } from "../../Member/Member.Current";
import { UserConversationsInterface, UsersModelStoreInterface } from "../Users.Model";

export class UsersModelSetDataHelpers {
  static isNewerThan<T extends { updatedAt: string | Date }>(item: T, stateItem: T): boolean {
    if (!item) return false;
    if (!stateItem) return true;
    return new Date(item.updatedAt) > new Date(stateItem.updatedAt);
  }

  static compileAllUsersSessionsAndEscalations = (
    state: UsersModelStoreInterface,
    users?: UserInterface[],
    sessions?: SessionInterface[],
    escalations?: EscalationInterface[],
  ) => {
    let dataArrays = {
      users: users || [],
      sessions: sessions || [],
      escalations: escalations || [],
    };

    dataArrays.users = DateHelper.fixDates<UserInterface[]>(dataArrays.users);
    dataArrays.sessions = DateHelper.fixDates<SessionInterface[]>(dataArrays.sessions);
    dataArrays.escalations = DateHelper.fixDates<EscalationInterface[]>(dataArrays.escalations);

    // Add the state data to the arrays if they exist.
    if (state.users) {
      dataArrays.users = dataArrays.users.concat(Object.values(state.users));
    }
    if (state.sessions) {
      dataArrays.sessions = dataArrays.sessions.concat(Object.values(state.sessions));
    }
    if (state.escalations) {
      dataArrays.escalations = dataArrays.escalations.concat(Object.values(state.escalations));
    }

    for (const session of dataArrays.sessions) {
      if (session.user) {
        dataArrays.users.push(session.user);
      }
      if (session.escalation) {
        dataArrays.escalations.push(session.escalation);
      }
    }
    for (const escalation of dataArrays.escalations) {
      if (escalation.user) {
        dataArrays.users.push(escalation.user);
      }
      if (escalation.session) {
        dataArrays.sessions.push(escalation.session);
      }
    }
    let dataMap = {
      users: new Map<number, UserInterface>(),
      sessions: new Map<number, SessionInterface>(),
      escalations: new Map<number, EscalationInterface>(),
    };
    for (const user of dataArrays.users) {
      if (!dataMap.users.has(user.id) || this.isNewerThan(user, dataMap.users.get(user.id)!)) {
        dataMap.users.set(user.id, user);
      }
    }

    for (const escalation of dataArrays.escalations) {
      if (!dataMap.escalations.has(escalation.id) || this.isNewerThan(escalation, dataMap.escalations.get(escalation.id)!)) {
        dataMap.escalations.set(escalation.id, escalation);
      }
    }
    for (const session of dataArrays.sessions) {
      if (!dataMap.sessions.has(session.id) || this.isNewerThan(session, dataMap.sessions.get(session.id)!)) {
        // remove escalation from session if it is resolved
        if (session.escalation && session.escalation.resolved) {
          session.escalation = undefined;
          session.escalationId = undefined; // Ensure escalationId is also cleared
        }

        dataMap.sessions.set(session.id, session);
      }
    }

    return dataMap;
  };

  static createUserConversationsFromDataMap(dataMap: ReturnType<typeof UsersModelSetDataHelpers.compileAllUsersSessionsAndEscalations>): {
    [userId: number]: UserConversationsInterface;
  } {
    let conversations: { [userId: number]: UserConversationsInterface } = {};
    for (const [userId, user] of dataMap.users.entries()) {
      if (user.isArchived) {
        // Skip archived users
        continue;
      }
      if (!conversations[userId]) {
        conversations[userId] = {
          name: NameHelper.nameForUser(user),
          user: user,
          escalations: {},
          sessions: {},
        };
      } else {
        conversations[userId].user = user; // Update user data if it exists
        conversations[userId].name = NameHelper.nameForUser(user);
      }
    }

    // Need to make sure no new escalations haven't come in that are not on the sessions.
    for (const [escalationId, escalation] of dataMap.escalations.entries()) {
      if (escalation.userId && dataMap.users.has(escalation.userId)) {
        escalation.user = dataMap.users.get(escalation.userId)!; // Ensure escalation has user data
      }
      if (escalation.sessionId && dataMap.sessions.has(escalation.sessionId)) {
        const session = dataMap.sessions.get(escalation.sessionId)!;
        if (!session.escalation || this.isNewerThan(escalation, session.escalation!)) {
          session.escalation = escalation; // Ensure session has escalation data
          session.escalationId = escalation.id; // Ensure escalationId is set
          dataMap.sessions.set(session.id, session); // Update session in the map
        }
      }
    }

    for (const [sessionId, session] of dataMap.sessions.entries()) {
      if (!session.open) {
        continue; // Skip closed sessions
      }
      if (session.userId && dataMap.users.has(session.userId)) {
        session.user = dataMap.users.get(session.userId)!; // Ensure session has user data
      }

      if (!conversations[session.userId]) {
        conversations[session.userId] = {
          name: NameHelper.nameForUser(session.user),
          user: session.user,
          escalations: {},
          sessions: {},
        };
      }

      if (session.escalationId && dataMap.escalations.has(session.escalationId)) {
        const escalation = dataMap.escalations.get(session.escalationId)!;
        if (!escalation.resolved) {
          conversations[session.userId].escalations![escalation.id] = escalation;
          session.escalation = escalation; // Ensure session has escalation data
        } else {
          session.escalation = undefined; // Ensure session has no escalation if resolved
          session.escalationId = undefined; // Ensure escalationId is also cleared
        }
      } else {
        session.escalation = undefined; // Ensure session has no escalation if not found
        session.escalationId = undefined; // Ensure escalationId is also cleared
      }

      conversations[session.userId].sessions![session.channel] = session;
    }

    for (const [userId, user] of dataMap.users.entries()) {
      if (user.isArchived) {
        //remove archived users from conversations
        delete conversations[userId];
        continue;
      }
    }

    return conversations;
  }

  static mappedFromObjects<T>(map: Map<number, T>): { [id: number]: T } {
    const obj: { [id: number]: T } = {};
    map.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }

  static getUnacknowledgedEscalations(escalations: { [escalationId: number]: EscalationInterface } | undefined): EscalationInterface[] {
    if (!escalations) return [];
    let items = Object.values(escalations).filter((e) => {
      if (e.membersAttemptedIds?.includes(Member.id!) && !e.acknowledgedMemberIds?.includes(Member.id!)) {
        return true; // Already attempted by the current member
      }
      return false;
    });

    return items.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }

  static removeDeletedOrgMergedUsersFromConversations(state: UsersModelStoreInterface): UsersModelStoreInterface {
    let userIdsToRemove: number[] = [];
    for (const [_, user] of Object.entries(state.users || {})) {
      if (user && (user as any).deletedDate) {
        userIdsToRemove.push(user.id);
      }
    }
    for (const [_, session] of Object.entries(state.sessions || {})) {
      if (session && session.merged && session.merged.from) {
        userIdsToRemove.push(session.merged.from);
      }
    }

    if (userIdsToRemove.length) {
      for (const id of userIdsToRemove) {
        if (state.conversations && state.conversations[id]) {
          delete state.conversations[id];
        }
        if (state.users && state.users[id]) {
          delete state.users[id];
        }
        // Also remove any sessions associated with this user
        if (state.sessions) {
          for (const [sessionId, session] of Object.entries(state.sessions)) {
            if (session.userId === id) {
              delete state.sessions[Number(sessionId)];
            }
          }
        }
      }
    }

    return state;
  }

  static setLastMessageForConversations(state: UsersModelStoreInterface): UsersModelStoreInterface {
    if (!state.conversations) return state;
    for (const [userId, conversation] of Object.entries(state.conversations)) {
      let lastUserMessageAt: Date | undefined;
      let lastUserMessageChannel: Channel | undefined;
      let lastUserMessage: string | undefined;
      for (const [_, session] of Object.entries(conversation.sessions || {})) {
        if (session.channel === Channel.VOICE && session.open && session.escalationId) {
          // If there is an open voice session with an escalation, prioritize that
          lastUserMessageAt = new Date(); // Set to now since the call is active
          lastUserMessageChannel = Channel.VOICE;
          lastUserMessage = session.lastUserMessage || "On Call";
          break; // No need to check further sessions
        }
        if (session.lastUserMessageAt) {
          if (!lastUserMessageAt || new Date(session.lastUserMessageAt) > new Date(lastUserMessageAt)) {
            if (session.channel === Channel.VOICE && !session.lastUserMessageAt) {
              lastUserMessageAt = new Date(session.createdAt);
            } else {
              lastUserMessageAt = new Date(session.lastUserMessageAt);
            }

            lastUserMessageChannel = session.channel;
            lastUserMessage = session.lastUserMessage;
          }
        }
      }

      if (!lastUserMessageAt) {
        let user = state.users ? state.users[Number(userId)] : undefined;
        if (user && user.lastUserMessageAt) {
          lastUserMessageAt = new Date(user.lastUserMessageAt);
        } else if (user && user.createdAt) {
          lastUserMessageAt = new Date(user.createdAt);
        }
      }

      if (!lastUserMessageChannel) {
        let user = state.users ? state.users[Number(userId)] : undefined;
        if (user && user.channels && user.channels.length > 0) {
          lastUserMessageChannel = user.channels[0];
        } else {
          lastUserMessageChannel = Channel.WEB;
        }
      }

      state.conversations[userId].lastUserMessageChannel = lastUserMessageChannel;
      state.conversations[userId].lastUserMessageAt = lastUserMessageAt;
      state.conversations[userId].lastUserMessage = lastUserMessage || "";
    }

    return state;
  }
}
